import React from "react";
import { Box, Chip, Stack, Typography, alpha } from "@mui/material";
import type { StaffStudentBilling } from "../types";

/**
 * Right-aligned badge shown next to each student row in the Staff Timetable
 * Report. Never divides by 100; consumes the fully-shaped `billing` payload
 * from the backend so display strings match the CSV / PDF exports one-for-one.
 *
 * The four cases exercised by the backend `StaffTimetableBillingShapeTest`
 * (Monthly with amount, Class-wise with amount, Amount not set, Billing not
 * configured) all round-trip through here.
 */
export default function BillingBadge({ billing }: { billing: StaffStudentBilling }) {
  if (!billing.type) {
    return (
      <Chip
        size="small"
        label="Billing not configured"
        sx={(t) => ({
          bgcolor: alpha(t.palette.warning.main, 0.12),
          color: t.palette.warning.dark,
          fontWeight: 600,
          border: `1px solid ${alpha(t.palette.warning.main, 0.35)}`,
        })}
      />
    );
  }
  const isMonthly = billing.type === "monthly";
  return (
    <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
      <Chip
        size="small"
        label={billing.label ?? (isMonthly ? "Monthly" : "Class-wise")}
        sx={(t) => ({
          bgcolor: isMonthly
            ? alpha(t.palette.primary.main, 0.10)
            : alpha(t.palette.success.main, 0.12),
          color: isMonthly ? t.palette.primary.dark : t.palette.success.dark,
          fontWeight: 600,
          border: `1px solid ${
            isMonthly
              ? alpha(t.palette.primary.main, 0.25)
              : alpha(t.palette.success.main, 0.30)
          }`,
        })}
      />
      <Typography variant="body2" sx={{ color: "text.primary", fontWeight: 500 }}>
        {billing.formatted_amount
          ? `${billing.formatted_amount}${billing.frequency_label ?? ""}`
          : "Amount not set"}
      </Typography>
    </Stack>
  );
}

// Also export the wrapper Box for grid callers that want the same layout
// container without duplicating pl responsive spacing.
export function BillingBadgeCell({ billing }: { billing: StaffStudentBilling }) {
  return (
    <Box sx={{ pl: { xs: 3.5, sm: 0 } }}>
      <BillingBadge billing={billing} />
    </Box>
  );
}
